/**
 * A checkbox dropdown for one Channel Deep-dive filter: Campaign type or Campaign
 * (spec §4, R25). ChannelFilter's mechanics: a mousedown outside or Escape closes
 * it, and Escape hands focus back to the trigger. The trigger states the applied
 * selection without opening anything, "ทั้งหมด" or "เลือก N รายการ", counting only
 * values an option carries: R11 applies the URL ∩ the options, so a campaign id
 * from another channel or workspace (Review Focus 1) never reads as a filter, and
 * the next change drops it. From 10 options a search box filters them by label.
 * failed = the options could not be read (R8): disabled, nothing reads as applied,
 * and the reason sits under the field at full contrast (a disabled field is drawn
 * at half opacity). A control with no option at all is disabled: nothing to pick.
 *
 * @module components/channels/MultiSelectFilter
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { LOOKER_FIELD_CLASS, LOOKER_LABEL_CLASS } from '@/components/looker/LookerFilterBar';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** A short note after the label, in smaller type. */
  hint?: string;
}

interface Props {
  label: string;
  options: readonly MultiSelectOption[];
  selected: readonly string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  failed?: boolean;
}

/** From this many options a search box filters them (R25). */
const SEARCH_FROM = 10;

interface Dropdown {
  shown: boolean;
  query: string;
  setQuery: (q: string) => void;
  rootRef: React.RefObject<HTMLDivElement>;
  triggerRef: React.RefObject<HTMLButtonElement>;
  onTrigger: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

/** Open / closed and the search text. Closing clears the search, so the list reopens whole. */
const useDropdown = (off: boolean): Dropdown => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Every way out: turned off, a mousedown outside, Escape, the trigger. Stable, as both setters are.
  const close = useCallback((): void => {
    setOpen(false);
    setQuery('');
  }, []);
  // A control that turns off while open draws no list this render ...
  const shown = open && !off;
  // ... and is closed with its search cleared, so turning on again does not bring the list back by itself.
  useEffect(() => {
    if (off) close();
  }, [off, close]);

  // mousedown rather than click, so a drag that starts inside and ends outside is not leaving.
  useEffect(() => {
    if (!shown) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [shown, close]);

  // Escape from anywhere inside, a focused checkbox included. Focus goes back to the
  // trigger; otherwise it lands on <body> and the next Tab restarts at the top of the page.
  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key !== 'Escape' || !shown) return;
    e.stopPropagation();
    close();
    triggerRef.current?.focus();
  };
  return { shown, query, setQuery, rootRef, triggerRef, onKeyDown, onTrigger: () => (shown ? close() : setOpen(true)) };
};

interface PanelProps {
  label: string;
  options: readonly MultiSelectOption[];
  picked: readonly string[];
  query: string;
  onQuery: (q: string) => void;
  onToggle: (value: string) => void;
  onClear: () => void;
}

/**
 * Under 640px the list spans the page less a 1rem gutter instead of hanging off the
 * trigger, so an open list never scrolls a phone sideways (NFR-09); the root is the
 * positioning context only from sm up.
 */
const PANEL_CLASS =
  'absolute inset-x-4 z-50 mt-2 rounded-lg border border-neutral-200 bg-white p-1 text-sm shadow-lg ' +
  'sm:inset-x-auto sm:left-0 sm:w-80';

const OptionPanel: React.FC<PanelProps> = ({ label, options, picked, query, onQuery, onToggle, onClear }) => {
  const { t } = useTranslation();
  const needle = query.trim().toLowerCase();
  const visible = options.filter((o) => o.label.toLowerCase().includes(needle));
  return (
    <div role="group" aria-label={label} className={PANEL_CLASS}>
      {options.length >= SEARCH_FROM && (
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          aria-label={t('channels.filters.search')}
          placeholder={t('channels.filters.search')}
          className="mb-1 h-8 w-full rounded-md border border-neutral-300 px-2 text-ink-900 placeholder:text-neutral-700 focus:border-ink-950 focus:outline-none focus:ring-2 focus:ring-ink-950"
        />
      )}
      <div className="max-h-64 overflow-y-auto">
        {visible.map((o) => (
          <label key={o.value} className="flex cursor-pointer select-none items-start gap-2 rounded-md px-2 py-1.5 text-ink-900 hover:bg-neutral-50">
            <input
              type="checkbox"
              checked={picked.includes(o.value)}
              onChange={() => onToggle(o.value)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-ink-950 focus:ring-2 focus:ring-ink-950"
            />
            <span className="min-w-0 flex-1 break-words">{o.label}</span>
            {o.hint && <span className="shrink-0 text-xs text-neutral-700">{o.hint}</span>}
          </label>
        ))}
      </div>
      {picked.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="mt-1 w-full rounded-md border-t border-neutral-200 px-2 py-1.5 text-left text-xs font-medium text-neutral-700 hover:bg-neutral-50 hover:text-ink-950"
        >
          {t('channels.filters.clear')}
        </button>
      )}
    </div>
  );
};

export const MultiSelectFilter: React.FC<Props> = ({ label, options, selected, onChange, disabled = false, failed = false }) => {
  const { t } = useTranslation();
  const off = disabled || failed || options.length === 0;
  const d = useDropdown(off);
  // The applied selection, in option order; nothing applies while the options failed (R8).
  const picked: string[] = failed ? [] : options.filter((o) => selected.includes(o.value)).map((o) => o.value);
  const toggle = (value: string): void => {
    const on = new Set(picked.includes(value) ? picked.filter((v) => v !== value) : [...picked, value]);
    onChange(options.filter((o) => on.has(o.value)).map((o) => o.value));
  };
  // The Clear button leaves with the selection it clears. Focus goes to the trigger, as on Escape,
  // or it falls to <body> and the next Tab restarts at the top of the page.
  const clear = (): void => {
    onChange([]);
    d.triggerRef.current?.focus();
  };
  return (
    <div ref={d.rootRef} className="sm:relative" onKeyDown={d.onKeyDown}>
      <span className={LOOKER_LABEL_CLASS}>
        {label}
        {/* The label names the control and the text inside is its value (ChannelFilter's rule). */}
        <button
          ref={d.triggerRef}
          type="button"
          aria-label={label}
          aria-expanded={d.shown}
          aria-haspopup="true"
          disabled={off}
          onClick={d.onTrigger}
          className={clsx(LOOKER_FIELD_CLASS, 'inline-flex min-w-[8rem] items-center justify-between gap-2')}
        >
          <span className={clsx('truncate', picked.length > 0 && 'font-semibold text-ink-950')}>
            {picked.length === 0 ? t('channels.filters.all') : t('channels.filters.selected', { n: picked.length })}
          </span>
          <ChevronDown size={14} aria-hidden="true" className={clsx('shrink-0 text-neutral-700 transition-transform', d.shown && 'rotate-180')} />
        </button>
      </span>
      {failed && <p className="mt-1 text-xs font-medium text-error-700">{t('channels.filters.failed')}</p>}
      {d.shown && (
        <OptionPanel label={label} options={options} picked={picked} query={d.query} onQuery={d.setQuery}
          onToggle={toggle} onClear={clear} />
      )}
    </div>
  );
};
